import { z } from "zod";

/**
 * Tolt referral attribution carried from the browser (tolt.js click capture)
 * through Clerk sign-up `unsafeMetadata` to the `user.created` webhook, where
 * the Tolt customer is created server-side. Shared client/server module —
 * keep it free of server-only imports.
 */

export const TOLT_SIGNUP_METADATA_KEY = "toltReferral";

export const toltSignupMetadataSchema = z.object({
  partnerId: z.string().min(1),
  clickId: z.string().min(1).optional(),
});

export type ToltSignupMetadata = z.infer<typeof toltSignupMetadataSchema>;

interface ToltWindowData {
  click_id?: string | null;
  partner_id?: string | null;
  customer_id?: string | null;
}

declare global {
  interface Window {
    tolt_data?: ToltWindowData;
  }
}

/**
 * Reads tolt.js click data and shapes it as Clerk sign-up `unsafeMetadata`.
 * Returns undefined when the visitor did not arrive via a partner link (or
 * tolt.js was blocked), so callers can spread it without branching.
 */
export function getToltSignupUnsafeMetadata():
  | { [TOLT_SIGNUP_METADATA_KEY]: ToltSignupMetadata }
  | undefined {
  if (typeof window === "undefined") return undefined;
  const partnerId = window.tolt_data?.partner_id;
  if (!partnerId) return undefined;
  return {
    [TOLT_SIGNUP_METADATA_KEY]: {
      partnerId,
      clickId: window.tolt_data?.click_id ?? undefined,
    },
  };
}

/** Extracts the referral from a Clerk user's unsafe metadata; null if absent/invalid. */
export function parseToltSignupMetadata(
  unsafeMetadata: unknown
): ToltSignupMetadata | null {
  if (typeof unsafeMetadata !== "object" || unsafeMetadata === null) return null;
  const raw = (unsafeMetadata as Record<string, unknown>)[
    TOLT_SIGNUP_METADATA_KEY
  ];
  const parsed = toltSignupMetadataSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}
